import { defineStore } from 'pinia';
import { Alert } from '@/v2/plugins/alert';
import { AnalyticsEntity } from '../entities/analytics/index';
import { ConversationsCategories, ConversationsTypes } from '../entities/conversations/interface';
import moment from 'moment';
import { useAuthStore } from '../../../store/shared/auth-store';
import Message from '../entities/chats/message';

import { db } from '@/firebase';
import {
	collection,
	query,
	orderBy,
	limit,
	startAfter,
	getDocs,
	getDoc,
	getCountFromServer,
	onSnapshot,
	doc,
	where,
	collectionGroup,
} from 'firebase/firestore';
import { configs } from '@/v2/system-configs';
import { t } from '@/locales/index';

const escalationColors = {
	aiHandled: configs.brand === 'Linker' ? '#EC552F' : '#015bed',
	escalated: configs.brand === 'Linker' ? '#667085' : '#667085',
};

export const useAnalyticsStore = defineStore('AnalyticsStore', {
	state: () => ({
		mainDataLoading: false,
		mainData: null,
		conversationsDataLoading: false,
		conversationsData: null,
		messagesDataLoading: false,
		messagesData: null,
		aiEscalationsCountDataLoading: false,
		aiEscalationsCountData: {
			total: 0,
			aiHandled: 0,
			escalated: 0,
		},
		escalationColors: escalationColors,
	}),
	getters: {
		conversationsChartData(state) {
			const points =
				state.conversationsData?.chart?.conversation_analytics?.data?.[0]?.data_points;

			if (!points) return [];

			const getPointsByType = (type, category) =>
				points
					.filter(
						(p) =>
							p.conversation_type == type &&
							(!category || p.conversation_category == category),
					)
					.sort((a, b) => a.start - b.start);

			const getPointsTotal = () => {
				const totalPoints = [];

				points.forEach((p) => {
					const point = totalPoints.find((point) => point.start == p.start);

					if (point) {
						point.conversation = (point.conversation || 0) + (p.conversation || 0);
					} else {
						totalPoints.push({ ...p });
					}
				});

				return totalPoints;
			};

			const formatPoints = (p) => ({
				label: moment(p.start * 1000).format('YYYY-MM-DD'),
				value: p.conversation,
			});

			const data = [
				{
					key: 'total',
					color: 'rgba(236, 85, 47, 0.6)',
					data: getPointsTotal().map(formatPoints),
				},
				{
					key: 'free',
					color: 'rgba(219, 39, 119, 0.6)',
					data: getPointsByType(ConversationsTypes.FREE_TIER).map(formatPoints),
				},
				{
					key: 'marketing',
					color: 'rgba(22, 163, 74, 0.6)',
					data: getPointsByType(
						ConversationsTypes.REGULAR,
						ConversationsCategories.MARKETING,
					).map(formatPoints),
				},
				{
					key: 'utility',
					color: 'rgba(8, 145, 178, 0.6)',
					data: getPointsByType(
						ConversationsTypes.REGULAR,
						ConversationsCategories.UTILITY,
					).map(formatPoints),
				},
				{
					key: 'service',
					color: 'rgba(79, 70, 229, 0.6)',
					data: getPointsByType(
						ConversationsTypes.REGULAR,
						ConversationsCategories.SERVICE,
					).map(formatPoints),
				},
				{
					key: 'ads',
					color: 'rgba(79, 70, 229, 0.6)',
					data: getPointsByType(ConversationsTypes.REGULAR, ConversationsCategories.ADS).map(
						formatPoints,
					),
				},
			];

			return data;
		},
		conversationsCostChartData(state) {
			const points =
				state.conversationsData?.chart?.conversation_analytics?.data?.[0]?.data_points;

			if (!points) return [];

			const getPointsByType = (type, category) =>
				points
					.filter(
						(p) =>
							p.conversation_type == type &&
							(!category || p.conversation_category == category),
					)
					.sort((a, b) => a.start - b.start);

			const getPointsTotal = () => {
				const totalPoints = [];

				points.forEach((p) => {
					const point = totalPoints.find((point) => point.start == p.start);

					if (point) {
						point.cost = (point.cost || 0) + (p.cost || 0);
					} else {
						totalPoints.push({ ...p });
					}
				});

				return totalPoints;
			};

			const formatPoints = (p) => ({
				label: moment(p.start * 1000).format('YYYY-MM-DD'),
				value: p.cost,
			});

			const data = [
				{
					key: 'total',
					color: 'rgba(236, 85, 47, 0.6)',
					data: getPointsTotal().map(formatPoints),
				},
				{
					key: 'free',
					color: 'rgba(79, 70, 229, 0.6)',
					data: getPointsByType(ConversationsTypes.FREE_TIER).map(formatPoints),
				},
				{
					key: 'marketing',
					color: 'rgba(22, 163, 74, 0.6)',
					data: getPointsByType(
						ConversationsTypes.REGULAR,
						ConversationsCategories.MARKETING,
					).map(formatPoints),
				},
				{
					key: 'utility',
					color: 'rgba(8, 145, 178, 0.6)',
					data: getPointsByType(
						ConversationsTypes.REGULAR,
						ConversationsCategories.UTILITY,
					).map(formatPoints),
				},
				{
					key: 'service',
					color: 'rgba(79, 70, 229, 0.6)',
					data: getPointsByType(
						ConversationsTypes.REGULAR,
						ConversationsCategories.SERVICE,
					).map(formatPoints),
				},
				{
					key: 'ads',
					color: 'rgba(79, 70, 229, 0.6)',
					data: getPointsByType(ConversationsTypes.REGULAR, ConversationsCategories.ADS).map(
						formatPoints,
					),
				},
			];

			return data;
		},
		messagesChartData(state) {
			const points = [
				...(state.messagesData?.chart?.received?.analytics?.data_points || []),
				...(state.messagesData?.chart?.sent_and_delivered?.analytics?.data_points || []),
			];

			if (!points.length) return [];

			const getPointsByType = (type) =>
				points.filter((p) => p[type]).sort((a, b) => a.start - b.start);

			const formatPoints = (key) => (p) => ({
				label: moment(p.start * 1000).format('YYYY-MM-DD'),
				value: p[key],
			});

			const data = [
				{
					key: 'sent',
					color: 'rgba(71, 84, 103, 0.6)',
					data: getPointsByType('sent').map(formatPoints('sent')),
				},
				{
					key: 'delivered',
					color: 'rgba(6, 182, 212, 0.6)',
					data: getPointsByType('delivered').map(formatPoints('delivered')),
				},
				{
					key: 'read',
					color: 'rgba(23, 178, 106, 0.6)',
					data: getPointsByType('received').map(formatPoints('received')),
				},
				{
					key: 'failed',
					color: 'rgba(240, 68, 56, 0.6)',
					data: [],
				},
			];

			return data;
		},
		parsedAiEscalationsCountData(state) {
			const labels = [t('whatsapp.aiHandled'), t('whatsapp.escalated')];
			const datasets = [
				{
					data: [
						state.aiEscalationsCountData.aiHandled,
						state.aiEscalationsCountData.escalated,
					],
					backgroundColor: [escalationColors.aiHandled, escalationColors.escalated],
				},
			];
			return {
				labels,
				datasets,
			};
		},
	},
	actions: {
		async fetchMainStatistics(params) {
			this.mainDataLoading = true;
			try {
				this.mainData = await AnalyticsEntity.fetchMainStatistics(params);
			} catch (error) {
				Alert.error(error?.response?.data?.message);
			} finally {
				this.mainDataLoading = false;
			}
		},
		async fetchConversationsStatistics(params) {
			this.conversationsDataLoading = true;
			try {
				this.conversationsData = await AnalyticsEntity.fetchConversationsStatistics(params);
			} catch (error) {
				Alert.error(error?.response?.data?.message);
			} finally {
				this.conversationsDataLoading = false;
			}
		},
		async fetchMessagesStatistics(params) {
			this.messagesDataLoading = true;
			try {
				this.messagesData = await AnalyticsEntity.fetchMessagesStatistics(params);
			} catch (error) {
				Alert.error(error?.response?.data?.message);
			} finally {
				this.messagesDataLoading = false;
			}
		},
		async fetchMessages() {
			const authStore = useAuthStore();
			const tenantId = authStore.tenantId;
			try {
				// console.time('[WhatsApp V2] fetchMessages');
				const conversationsCollection = collectionGroup(
					db,
					// // `tenants/${tenantId}/conversations/${201096005233}/messages`,
					// `tenants`,
					// `${tenantId}`,
					`messages`,
				);
				const queryData = [
					conversationsCollection,
					where('__name__', '>', `tenants/${tenantId}/conversations/201096005233`),
				];
				// queryData.push(orderBy('timestamp', 'desc'));

				const total = (await getCountFromServer(query(...queryData))).data().count;

				// const res = await getDocs(query(...queryData));
				// // console.timeEnd('[WhatsApp V2] fetchMessages');
				// const data = res.docs.map((doc) => {
				// 	const message = new Message(doc.data());
				// 	return message;
				// });

				console.error({
					// data,
					totalRecords: total,
				});
			} catch (error) {
				console.error('[WhatsApp V2] error in Fetch Message of Conversation', error);
				throw error;
			}
		},
		async fetchAiEscalationsCount(params) {
			const authStore = useAuthStore();
			const tenantId = authStore.tenantId;
			this.aiEscalationsCountDataLoading = true;
			try {
				const startDate = new Date(
					Date.UTC(
						new Date(params.date_from).getUTCFullYear(),
						new Date(params.date_from).getUTCMonth(),
						new Date(params.date_from).getUTCDate(),
					),
				);
				const endDate = new Date(
					Date.UTC(
						new Date(params.date_to).getUTCFullYear(),
						new Date(params.date_to).getUTCMonth(),
						new Date(params.date_to).getUTCDate(),
					),
				);
				endDate.setDate(endDate.getDate() + 1); // Add 1 day to include the last day

				// console.log('startDate', startDate.getTime());
				// console.log('endDate', endDate.getTime());

				const monthlyStatsRef = collection(
					db,
					`tenants/${tenantId}/channels/whatsapp/monthly-statistics`,
				);
				const dailyStatsRef = collection(
					db,
					`tenants/${tenantId}/channels/whatsapp/daily-statistics`,
				);

				// Get complete months data
				const monthlySnapshot = await getDocs(
					query(
						monthlyStatsRef,
						where('startTimestamp', '>=', startDate.getTime()),
						where('endTimestamp', '<=', endDate.getTime()),
					),
				);

				// console.log('monthlySnapshot', monthlySnapshot);

				// Use Set to track unique conversation IDs
				const uniqueConversations = new Set();
				const escalatedConversations = new Set();

				// Handle complete months
				monthlySnapshot.docs.forEach((doc) => {
					const data = doc.data();
					data.aiRepliedConversationsIds?.forEach((id) => uniqueConversations.add(id));
					data.escalatedConversationsIds?.forEach((id) => escalatedConversations.add(id));
				});

				// Handle partial start month
				if (startDate.getDate() !== 1) {
					// console.log('startDate', startDate);
					const startMonthLastDay = new Date(
						startDate.getFullYear(),
						startDate.getMonth() + 1,
						0,
					);

					const endRange = Math.min(startMonthLastDay.getTime(), endDate.getTime());
					// console.log(`Range1: [${startDate.getTime()}, ${endRange}]`);

					const startMonthDailySnapshot = await getDocs(
						query(
							dailyStatsRef,
							where('startTimestamp', '>=', startDate.getTime()),
							where('endTimestamp', '<=', endRange),
						),
					);
					// console.log('startMonthDailySnapshot', startMonthDailySnapshot);

					startMonthDailySnapshot.docs.forEach((doc) => {
						const data = doc.data();
						data.aiRepliedConversationsIds?.forEach((id) => uniqueConversations.add(id));
						data.escalatedConversationsIds?.forEach((id) => escalatedConversations.add(id));
					});
				}

				// Handle partial end month
				if (
					new Date(params.date_to).getDate() !==
					new Date(endDate.getFullYear(), new Date(params.date_to).getMonth() + 1, 0).getDate()
				) {
					// console.log('endDate', endDate);
					const endMonthFirstDay = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

					const startRange = Math.max(endMonthFirstDay.getTime(), startDate.getTime());
					// console.log(`Range2: [${startRange}, ${endDate.getTime()}]`);

					const endMonthDailySnapshot = await getDocs(
						query(
							dailyStatsRef,
							where('startTimestamp', '>=', startRange),
							where('endTimestamp', '<=', endDate.getTime()),
						),
					);

					// console.log('endMonthDailySnapshot', endMonthDailySnapshot);

					endMonthDailySnapshot.docs.forEach((doc) => {
						const data = doc.data();
						data.aiRepliedConversationsIds?.forEach((id) => uniqueConversations.add(id));
						data.escalatedConversationsIds?.forEach((id) => escalatedConversations.add(id));
					});
				}
				this.aiEscalationsCountData = {
					total: uniqueConversations.size,
					aiHandled: uniqueConversations.size - escalatedConversations.size,
					escalated: escalatedConversations.size,
				};
			} catch (error) {
				console.error(error);
				// Alert.error(error?.response?.data?.message);
			} finally {
				this.aiEscalationsCountDataLoading = false;
			}
		},
	},
});
